import {
  getFeaturedItemPollIntervalId,
  getFeaturedItemPollingProgress,
} from './selectors'

const DALMATIAN_URL = process.env.REACT_APP_DALMATIAN_API2_URL ?? ''

export const FETCH_FEATURED_ITEMS_LOADING = 'FETCH_FEATURED_ITEMS_LOADING'
const fetchFeaturedItemsLoading = () => ({
  type: FETCH_FEATURED_ITEMS_LOADING,
})

export const FETCH_FEATURED_ITEMS_SUCCESS = 'FETCH_FEATURED_ITEMS_SUCCESS'
const fetchFeaturedItemsSuccess = response => ({
  type: FETCH_FEATURED_ITEMS_SUCCESS,
  payload: response,
})

export const FETCH_FEATURED_ITEMS_ERROR = 'FETCH_FEATURED_ITEMS_ERROR'
const fetchFeaturedItemsError = err => ({
  type: FETCH_FEATURED_ITEMS_ERROR,
  error: true,
  payload: err,
})

/**
 * Fetch all featured items on a study's dashboard
 * @param {String} studyId - ID of the study, i.e. FRE0123
 */
export const fetchFeaturedItems = studyId => async (dispatch, getState, { fetch }) => {
  dispatch(fetchFeaturedItemsLoading())

  const response = await fetch(`${DALMATIAN_URL}/study_items/${studyId}/featured_items/`)

  if (!response.ok) {
    dispatch(fetchFeaturedItemsError(response.statusText))
    return
  }

  const data = await response.json()
  dispatch(fetchFeaturedItemsSuccess(data))
}

export const SET_FEATURED_ITEMS_POLL_INTERVAL_ID = 'SET_FEATURED_ITEMS_POLL_INTERVAL_ID'
const __setFeaturedItemsPollIntervalId = (intervalId) => ({
  type: SET_FEATURED_ITEMS_POLL_INTERVAL_ID,
  intervalId,
})

export const STOP_FEATURED_ITEMS_POLLING = 'STOP_FEATURED_ITEMS_POLLING'
const __stopFeaturedItemsPolling = () => ({
  type: STOP_FEATURED_ITEMS_POLLING,
})

export const POLL_FEATURED_ITEM_STATUS = 'POLL_FEATURED_ITEM_STATUS'
const __pollFeaturedItemsStatus = (complete, total) => ({
  type: POLL_FEATURED_ITEM_STATUS,
  complete,
  total,
})

export const stopFeaturedItemPolling = () => (dispatch, getState) => {
  const intervalId = getFeaturedItemPollIntervalId(getState())
  clearInterval(intervalId)
  dispatch(__stopFeaturedItemsPolling())
}

/**
 * Start polling for featured items generated by auto summaries.
 *
 * @param {String} studyId - ID of the study, i.e FRE0123
 * @param {Boolean} [force=false] - Poll even if there is no summary report yet. Useful managing race condition after data file ingestion.
 */
export const startFeaturedItemPolling = (
  studyId,
  force = false
) => (dispatch, getState, { fetch }) => {
  const isPolling = Boolean(getFeaturedItemPollIntervalId(getState()))

  if (isPolling) {
    return
  }

  const intervalId = setInterval(async () => {
    const statusResponse = await fetch(`${DALMATIAN_URL}/study_content/${studyId}/`)

    if (!statusResponse.ok) {
      dispatch(stopFeaturedItemPolling())
      return
    }

    const previousStatus = getFeaturedItemPollingProgress(getState())
    const statusData = await statusResponse.json()

    if (statusData.summaries.length === 0 && !force) {
      dispatch(stopFeaturedItemPolling())
      return
    }

    const { complete = 0, total = 0 } = statusData.summaries?.[0] ?? {}
    dispatch(__pollFeaturedItemsStatus(complete, total))

    if (previousStatus.complete !== complete) {
      const itemResponse = await fetch(
        `${DALMATIAN_URL}/study_items/${studyId}/featured_items/`
      )

      if (!itemResponse.ok) {
        dispatch(stopFeaturedItemPolling())
        return
      }

      const data = await itemResponse.json()
      dispatch(fetchFeaturedItemsSuccess(data))
    }

    if (total !== 0 && complete === total) {
      dispatch(stopFeaturedItemPolling())
    }
  }, 5000)

  dispatch(__setFeaturedItemsPollIntervalId(intervalId))
}

export const CREATE_FEATURED_ITEM_LOADING = 'CREATE_FEATURED_ITEM_LOADING'
const createFeaturedItemLoading = () => ({
  type: CREATE_FEATURED_ITEM_LOADING,
})

export const CREATE_FEATURED_ITEM_SUCCESS = 'CREATE_FEATURED_ITEM_SUCCESS'
const createFeaturedItemSuccess = featuredItem => ({
  type: CREATE_FEATURED_ITEM_SUCCESS,
  payload: featuredItem,
})

export const CREATE_FEATURED_ITEM_ERROR = 'CREATE_FEATURED_ITEM_ERROR'
const createFeaturedItemError = err => ({
  type: CREATE_FEATURED_ITEM_ERROR,
  error: true,
  payload: err,
})

/**
 * Attach a new featured item to a study's dashboard.
 * @param {Object} featuredItem - Content type+object of the featured item
 * @param {String} featuredItem.content_type - Type of featured item content
 * @param {Object} featuredItem.content_object - Data of the featured item
 * @param {String} featuredItem.title - Name of the featured item
 * @param {String} studyId - ID of the study, i.e. FRE0123
 */
export const createFeaturedItem = (featuredItem, studyId) => async (
  dispatch,
  getState,
  { fetch }
) => {
  dispatch(createFeaturedItemLoading())

  const response = await fetch(`${DALMATIAN_URL}/study_items/${studyId}/featured_items/`, {
    method: 'POST',
    jsonBody: featuredItem,
  })

  if (!response.ok) {
    dispatch(createFeaturedItemError(response.statusText))
    return
  }

  const data = await response.json()
  dispatch(createFeaturedItemSuccess(data))
}

export const UPDATE_FEATURED_ITEM_LOADING = 'UPDATE_FEATURED_ITEM_LOADING'
const updateFeaturedItemLoading = () => ({
  type: UPDATE_FEATURED_ITEM_LOADING,
})

export const UPDATE_FEATURED_ITEM_SUCCESS = 'UPDATE_FEATURED_ITEM_SUCCESS'
const updateFeaturedItemSuccess = featuredItem => ({
  type: UPDATE_FEATURED_ITEM_SUCCESS,
  payload: featuredItem,
})

export const UPDATE_FEATURED_ITEM_ERROR = 'UPDATE_FEATURED_ITEM_ERROR'
const updateFeaturedItemError = err => ({
  type: UPDATE_FEATURED_ITEM_ERROR,
  error: true,
  payload: err,
})

/**
 * Attach a new featured item to a study's dashboard.
 * @param {Object} featuredItem - Content type+object of the featured item
 * @param {String} featuredItem.content_type - Type of featured item content
 * @param {Object} featuredItem.content_object - Data of the featured item
 * @param {String} featuredItem.title - Name of the featured item
 * @param {number} featuredItem.position - Index of the featured item
 * @param {String} studyId - ID of the study, i.e. FRE0123
 */
export const updateFeaturedItem = (featuredItem, studyId) => async (
  dispatch,
  getState,
  { fetch }
) => {
  dispatch(updateFeaturedItemLoading())

  // Strip analysis and stats data from any patch, they are not necessary and the php proxy has trouble
  // with passing through large bodies
  const itemCopy =
    featuredItem.content_object && featuredItem.content_type === 'linkedanalysis'
      ? {
          ...featuredItem,
          content_object: {
            ...featuredItem.content_object,
            analysis_data: null,
            stats_data: null,
          },
        }
      : { ...featuredItem }

  const response = await fetch(
    `${DALMATIAN_URL}/study_items/${studyId}/featured_items/${featuredItem.id}/`,
    {
      method: 'PATCH',
      jsonBody: itemCopy,
    }
  )

  if (!response.ok) {
    dispatch(updateFeaturedItemError(response.statusText))
    return
  }

  const data = await response.json()
  dispatch(updateFeaturedItemSuccess(data))
}

export const DELETE_FEATURED_ITEM_LOADING = 'DELETE_FEATURED_ITEM_LOADING'
const deleteFeaturedItemLoading = featuredItemId => ({
  type: DELETE_FEATURED_ITEM_LOADING,
  payload: {
    id: featuredItemId,
  },
})

export const DELETE_FEATURED_ITEM_SUCCESS = 'DELETE_FEATURED_ITEM_SUCCESS'
const deleteFeaturedItemSuccess = () => ({
  type: DELETE_FEATURED_ITEM_SUCCESS,
})

export const DELETE_FEATURED_ITEM_ERROR = 'DELETE_FEATURED_ITEM_ERROR'
const deleteFeaturedItemError = err => ({
  type: DELETE_FEATURED_ITEM_ERROR,
  error: true,
  payload: err,
})

/**
 * Delete a featured item from a study dashboard
 * @param {String} featuredItemId - UUID of the featured item
 * @param {String} studyId - ID of the study, i.e. FRE0123
 */
export const deleteFeaturedItem = (featuredItemId, studyId) => async (
  dispatch,
  getState,
  { fetch }
) => {
  dispatch(deleteFeaturedItemLoading(featuredItemId))

  const response = await fetch(
    `${DALMATIAN_URL}/study_items/${studyId}/featured_items/${featuredItemId}/`,
    { method: 'DELETE' }
  )

  if (!response.ok) {
    dispatch(deleteFeaturedItemError(response.statusText))
    return
  }

  dispatch(deleteFeaturedItemSuccess())
}

export const DELETE_ALL_FEATURED_ITEMS_LOADING = 'DELETE_ALL_FEATURED_ITEMS_LOADING'
const __deleteAllFeaturedItemsLoading = () => ({
  type: DELETE_ALL_FEATURED_ITEMS_LOADING,
})

export const DELETE_ALL_FEATURED_ITEMS_SUCCESS = 'DELETE_ALL_FEATURED_ITEMS_SUCCESS'
const __deleteAllFeaturedItemsSuccess = () => ({
  type: DELETE_ALL_FEATURED_ITEMS_SUCCESS,
})

export const DELETE_ALL_FEATURED_ITEMS_ERROR = 'DELETE_ALL_FEATURED_ITEMS_ERROR'
const __deleteAllFeaturedItemsError = (error) => ({
  type: DELETE_ALL_FEATURED_ITEMS_ERROR,
  error,
})

export const deleteAllFeaturedItems = (studyId) => async (dispatch, getState, { fetch }) => {
  dispatch(__deleteAllFeaturedItemsLoading())

  const response = await fetch(
    `${DALMATIAN_URL}/study_items/${studyId}/featured_items/`,
    { method: 'DELETE' }
  )

  if (!response.ok) {
    const error = new Error('Failed to delete featured items', { cause: response })
    dispatch(__deleteAllFeaturedItemsError(error))
    throw error
  }

  dispatch(__deleteAllFeaturedItemsSuccess())
  window.analytics.track('Delete All Featured Items', { studyId })
}

export const UPDATE_FEATURED_ITEM_CARD_POSITION = 'UPDATE_FEATURED_ITEM_CARD_POSITION'
/**
 * Move a featured item to a different order in the study dashboard
 * @param {number} newPosition - Intended new position of the card
 * @param {number} atPosition - Current position of the card
 * @param {Object} card - Card data
 */
export const updateFeaturedItemCardPosition = (newPosition, atPosition, card) => ({
  type: UPDATE_FEATURED_ITEM_CARD_POSITION,
  payload: {
    newPosition,
    atPosition,
    card,
  },
})
